import React, { useState } from 'react';
import {
  shortPickKindLabel,
  type OrderPullException,
  type OrderPullExceptionKind,
  type OrderPullExceptionTarget,
} from '../../api/orderPull';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';

interface OrderPullShortPickProps {
  cardName: string;
  targets: OrderPullExceptionTarget[];
  busyLineIds: ReadonlySet<string>;
  enabled: boolean;
  onRecord: (
    lineId: string,
    kind: OrderPullExceptionKind,
    qty: number,
    note: string,
  ) => void;
}

function targetLabel(cardName: string, target: OrderPullExceptionTarget, several: boolean): string {
  if (!several || target.slot_number == null) {
    return cardName;
  }
  return `${cardName} slot ${target.slot_number}`;
}

function exceptionText(exc: OrderPullException): string {
  const words = `${shortPickKindLabel(exc.kind)} ${exc.qty}`;
  return exc.note ? `${words} — ${exc.note}` : words;
}

function stopRowToggle(event: React.SyntheticEvent) {
  event.stopPropagation();
}

export const OrderPullShortPick: React.FC<OrderPullShortPickProps> = ({
  cardName,
  targets,
  busyLineIds,
  enabled,
  onRecord,
}) => {
  if (targets.length === 0) {
    return null;
  }
  const several = targets.length > 1;
  return (
    <span
      className={several ? 'order-pull-short-pick order-pull-short-pick-stack' : 'order-pull-short-pick'}
      style={several ? styles.stack : styles.wrap}
      onClick={stopRowToggle}
      onMouseDown={stopRowToggle}
    >
      {targets.map((target) => (
        <ShortPickTarget
          key={target.line_id}
          label={targetLabel(cardName, target, several)}
          target={target}
          showSlot={several && target.slot_number != null}
          stacked={several}
          busy={busyLineIds.has(target.line_id)}
          enabled={enabled}
          onRecord={onRecord}
        />
      ))}
    </span>
  );
};

function ShortPickTarget({
  label,
  target,
  showSlot,
  stacked,
  busy,
  enabled,
  onRecord,
}: {
  label: string;
  target: OrderPullExceptionTarget;
  showSlot: boolean;
  stacked: boolean;
  busy: boolean;
  enabled: boolean;
  onRecord: OrderPullShortPickProps['onRecord'];
}) {
  const openQty = Math.max(0, target.open_qty);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const closed = !enabled || openQty < 1 || busy;
  const sendQty = Math.min(Math.max(1, qty), Math.max(openQty, 1));

  return (
    <span
      className="order-pull-short-pick-target"
      style={stacked ? styles.targetStack : styles.target}
    >
      {showSlot && <span style={styles.slotLabel}>Slot {target.slot_number}</span>}
      {target.exceptions.map((exc) => (
        <span key={exc.id} style={styles.flag}>
          {exceptionText(exc)}
        </span>
      ))}
      <input
        type="number"
        min={1}
        max={openQty || 1}
        value={qty}
        disabled={closed}
        aria-label={`Short-pick quantity for ${label}`}
        style={styles.qty}
        onClick={stopRowToggle}
        onChange={(event) => setQty(Number(event.target.value) || 1)}
      />
      <input
        type="text"
        value={note}
        disabled={closed}
        placeholder="Note"
        aria-label={`Short-pick note for ${label}`}
        style={styles.note}
        onClick={stopRowToggle}
        onChange={(event) => setNote(event.target.value)}
      />
      <button
        type="button"
        disabled={closed}
        aria-label={`Mark ${label} missing`}
        style={styles.button}
        onClick={(event) => {
          stopRowToggle(event);
          onRecord(target.line_id, 'missing', sendQty, note);
        }}
      >
        Missing
      </button>
      <button
        type="button"
        disabled={closed}
        aria-label={`Mark ${label} damaged`}
        style={styles.button}
        onClick={(event) => {
          stopRowToggle(event);
          onRecord(target.line_id, 'damaged', sendQty, note);
        }}
      >
        Damaged
      </button>
    </span>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'inline',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: spacing.xs,
  },
  target: {
    display: 'inline',
  },
  targetStack: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '2px',
  },
  slotLabel: {
    marginRight: spacing.xs,
    fontSize: typography.caption.fontSize,
    lineHeight: '16px',
    color: colors.textSecondary,
  },
  flag: {
    marginRight: spacing.xs,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    lineHeight: '16px',
    color: colors.text,
  },
  qty: {
    width: 36,
    height: 20,
    marginRight: 2,
    padding: 0,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.caption.fontSize,
    lineHeight: '16px',
    verticalAlign: 'middle',
  },
  note: {
    width: 52,
    height: 20,
    marginRight: 2,
    padding: `0 ${spacing.xs}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.caption.fontSize,
    lineHeight: '16px',
    verticalAlign: 'middle',
  },
  button: {
    height: 20,
    marginRight: 2,
    padding: `0 ${spacing.xs}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.backgroundAlt,
    color: colors.text,
    cursor: 'pointer',
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    lineHeight: '16px',
    verticalAlign: 'middle',
  },
};
